import { Component, ElementRef, enableProdMode, OnInit, DOCUMENT } from '@angular/core';
import { AuthService } from './core/account/auth.service';
import { GlobalService } from './shared/services/global.service';
import { TenantService } from './tenant/tenant.service';
import { PLATFORM_ID, APP_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ChildrenOutletContexts, NavigationEnd, NavigationStart, Router, RouterEvent, RouterOutlet } from '@angular/router';
import * as jspdf from 'jspdf';
import html2canvas from 'html2canvas';
import html2pdf from 'html2pdf.js'
import { slider } from './slider';
import { formatNumber, Location } from '@angular/common';
import { Event } from '@angular/router';


import {
  transition,
  trigger,
  query,
  style,
  animate,
  group,
  animateChild
} from '@angular/animations';
import { LocationStrategy } from '@angular/common';

import { filter } from 'rxjs/operators';
import { AppService } from './shared/services/app.service';
import { TenantResponse } from './tenant/tenant.model';
import { SSL_OP_SSLEAY_080_CLIENT_DH_BUG } from 'constants';
import { Browser } from '@capacitor/browser';
import { SwUpdate } from '@angular/service-worker';

declare global {
  interface Window {
    handlePushNotification: (payload: string) => void;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    slider
  ],
  standalone: false
})
export class AppComponent implements OnInit {
  disableAnimation: true;
  title = 'Church Services';
  isLoading: boolean = true;
  language: string = "English";
  simpleTenant: any;
  currentTenant: string;

  private readonly isAnimated: boolean = false;
  public animationState: string;
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private swUpdate: SwUpdate,
    private authService: AuthService,
    public globalService: GlobalService,
    private tenantService: TenantService,
    private router: Router,
    private location: Location,
    private contexts: ChildrenOutletContexts,
    private el: ElementRef,
    private appService: AppService

  ) {

    if (swUpdate.isEnabled) {
      swUpdate.versionUpdates.subscribe(() => {
        swUpdate.activateUpdate().then(() => {
          document.location.reload();
        });
      });
    }

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        // ✅ reset AFTER full navigation completes
        this.globalService.isBack = false;
      }
    });

    // Prevent install PWA to home screen prompt
    window.addEventListener('beforeinstallprompt', (e) => {
      // Prevent the default browser prompt
      e.preventDefault();
    });

    window.handlePushNotification = (payload: string) => {
      const data = JSON.parse(payload);
      if (data.route) {
        this.router.navigate([data.route]); // Angular navigation
      }
    };

  }

  private clickHandler = (e: any) => {
    const link = e.target.closest('a');
    if (!link || !link.href) return;

    const isExternal =
      link.href.startsWith('http') &&
      !link.href.includes(window.location.host);

    if (isExternal) {
      e.preventDefault();
      Browser.open({ url: link.href });
    }
  };

  ngOnDestroy() {
    document.removeEventListener('click', this.clickHandler);
  }

  async ngOnInit() {
    this.isLoading = true;

    let tenant = '';
    let tenantList = await this.tenantService.getAllTenantNames();
    this.tenantService.setTenantNames(tenantList);

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SW_UPDATED') {
          // Force refresh so new index.html + bundles load
          window.location.reload();
        }
      });

      navigator.serviceWorker.ready.then(registration => {
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      });

      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
    }

    document.addEventListener('click', this.clickHandler);
    tenant = this.tenantService.getTenant();

    if (tenant.toLowerCase().trim() != 'main') {
      localStorage.setItem('tenant', tenant);
      this.globalService.subdomain = tenant;
    }

    var defaultLanguage = localStorage.getItem('Language');
    if (defaultLanguage != null && defaultLanguage.trim() != "")
      this.language = defaultLanguage;

    this.globalService.SetRTL(this.globalService.currentLanguage);
    this.globalService.resourceArr = await this.globalService.getResources(
      this.globalService.currentLanguage
    );

    var localTenant = localStorage.getItem('tenant');
    if (tenant.toLowerCase().trim() == 'main')
      tenant = localTenant ?? '';

    if (localTenant?.toLowerCase().trim() == 'main' || (this.tenantService.tenantNamesCache?.includes(localTenant?.toLowerCase().trim()??"") == false))
    {
      localStorage.removeItem('tenant');
      localTenant = '';
      tenant = '';
    }

    if (tenant != null && tenant != "") {
      // if url tenant exists and different than localstorage tenant then switch and clear cache
      if (localTenant == null || localTenant.trim() == '' || ((localTenant != null && localTenant.trim() != "") &&
        (tenant.trim().toUpperCase() != localTenant.trim().toUpperCase()))) {
        if (tenant.toLowerCase().trim() != 'main') {
          localStorage.setItem('tenant', tenant);
          this.globalService.subdomain = tenant;
        }
        this.currentTenant = tenant;
        await this.appService.clearAllCaches();
        this.authService.logout(0);
      }
      else
        // if url tenant exists and is the same as localstorage tenant then just set subdomain and current to the same
        if ((tenant != null && tenant != "" && localTenant != null && localTenant.trim() != ""
          && tenant.trim().toUpperCase() == localTenant.trim().toUpperCase())) {
          this.globalService.subdomain = localTenant;
          this.currentTenant = localTenant;
        }
    }
    // if url tenant does not exist and localstorage tenant exists then set subdomain and current to the local
    else if (localTenant != null && localTenant.trim() != "") {
      this.globalService.subdomain = localTenant;
      this.currentTenant = localTenant;
    }
    else { 
      this.router.navigate(['main/switch-tenant']);
      this.isLoading = false;
      return;
    }
    // }

    this.router.events
      .pipe(
        filter((e: Event): e is NavigationStart => e instanceof NavigationStart)
      )
      .subscribe({
        next: (ev) => {
          if (!this.currentTenant || this.currentTenant.trim() === '') {
            this.currentTenant = localStorage.getItem('tenant')?.toLowerCase() || '';
          }

          let url = ev.url.toLowerCase();

          const segments = url.split('/').filter(x => x);
          const firstSegment = segments[0];

          const isTenantRoute =
            firstSegment &&
            this.tenantService.tenantNamesCache?.includes(firstSegment);

          if (
            this.currentTenant &&
            !isTenantRoute &&
            firstSegment !== 'main'
          ) {
            this.router.navigate([`/${this.currentTenant}`, ...segments]);
          }
        }
      });

    let url = window.location.pathname.toLowerCase();
    if (url === '/') url = '/home';

    const segments = url.split('/').filter(x => x);
    const firstSegment = segments[0];

    // ✅ determine if first segment is a valid tenant
    const isTenantRoute =
      firstSegment &&
      this.tenantService.tenantNamesCache?.includes(firstSegment);

    if (
      this.currentTenant &&
      !isTenantRoute &&
      firstSegment !== 'main'
    ) {
      this.router.navigate([`/${this.currentTenant}`, ...segments]);
      this.isLoading = false;
      return;
    }

    this.globalService.mainSelector = this.el.nativeElement; //.querySelector('.main');
    this.simpleTenant = await this.tenantService.getSimpleTenant();
    var csModule = this.simpleTenant.cmsModule;

    this.globalService.timeZone = this.simpleTenant.timeZoneDifference;


    if ((csModule == false) && (this.simpleTenant.subdomain.toLowerCase().trim() != "main") && (url.toUpperCase().trim().indexOf('VALIDATE') < 0) && (url.toUpperCase().trim().indexOf('HOME') < 0)
      && (url.toUpperCase().trim().indexOf('RESETPASSWORD') < 0) && (url.toUpperCase().trim().indexOf('SIGNUP') < 0)
      && (url.toUpperCase().trim().indexOf('FAST-CHECKIN') < 0) && (url.toUpperCase().trim().indexOf('FORMRENDER') < 0)
      && (url.toUpperCase().trim().indexOf('UNSUBSCRIBE') < 0) && (url.toUpperCase().trim().indexOf('ADD-POINT') < 0)
      && (url.toUpperCase().trim().indexOf('QR-SELF-CHECKIN') < 0) && (url.toUpperCase().trim().indexOf('QR-SELF-CHECKIN-FIXED') < 0) && (url.toUpperCase().trim().indexOf('QR-SELF-CHECKIN-FIXED-GROUP') < 0)
      && (url.toUpperCase().trim().indexOf('QR-SELF-CHECKIN-FIXED-VENUE') < 0)
      && (url.toUpperCase().trim().indexOf('NOTIFICATION') < 0) && (url.toUpperCase().trim().indexOf('NOTIFICATION') < 0)) {
      await this.authService.autoLogin(1);
    }

    if (this.simpleTenant.deleted == true) {
      this.router.navigate([this.currentTenant + '/service-notfound']);
      this.isLoading = false;
      return;
    }

    this.isLoading = false;
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet?.activatedRouteData?.['animation'];
    // return outlet && outlet.activatedRouteData && outlet.activatedRouteData.animation;
  }

  getRouteDataText() {
    return this.contexts?.getContext('primary')?.route?.snapshot?.data?.['animation'];
  }

}
